export const JWP_ASSET_BASE_URL = "https://assets.jwpepper.com";
export const DEFAULT_COVER_IMAGE_URL = `${JWP_ASSET_BASE_URL}/covers/default-cover-image.jpg`;

const coverUrlCache = new Map();

function normalizeAssetBase(assetBase) {
  return String(assetBase || JWP_ASSET_BASE_URL).replace(/\/$/, "");
}

function defaultCoverFor(assetBase) {
  return `${normalizeAssetBase(assetBase)}/covers/default-cover-image.jpg`;
}

function cacheKey(assetBase, skuId) {
  return `${assetBase}|${skuId}`;
}

/**
 * Resolve a manifest `cover.path` against the assets CDN.
 * @param {string} path
 * @param {string} [assetBase]
 * @returns {string}
 */
export function coverImageUrlFromPath(path, assetBase = JWP_ASSET_BASE_URL) {
  const base = `${normalizeAssetBase(assetBase)}/`;
  const relativePath = path != null ? String(path).trim() : "";
  if (!relativePath) {
    return defaultCoverFor(assetBase);
  }
  try {
    return new URL(relativePath, base).href;
  } catch (_error) {
    return defaultCoverFor(assetBase);
  }
}

/**
 * Load the cover image URL from `assets.jwpepper.com/products/<skuId>.json`.
 * Results (including fallbacks) are cached per SKU.
 * @param {string | null | undefined} skuId
 * @param {{ assetBase?: string, defaultCover?: string }} [options]
 * @returns {Promise<string>}
 */
export async function coverUrlFromManifest(skuId, options = {}) {
  const assetBase = normalizeAssetBase(options.assetBase);
  const defaultCover = options.defaultCover || defaultCoverFor(assetBase);
  const id = skuId != null ? String(skuId).trim() : "";
  if (!id) {
    return defaultCover;
  }

  const key = cacheKey(assetBase, id);
  const cached = coverUrlCache.get(key);
  if (cached) {
    return cached;
  }

  const pending = (async () => {
    try {
      const response = await fetch(
        `${assetBase}/products/${encodeURIComponent(id)}.json`,
      );
      if (!response.ok) {
        return defaultCover;
      }
      const manifest = await response.json();
      const path = manifest && manifest.cover && manifest.cover.path;
      if (!path) {
        return defaultCover;
      }
      return coverImageUrlFromPath(path, assetBase);
    } catch (_error) {
      return defaultCover;
    }
  })();

  coverUrlCache.set(key, pending);
  return pending;
}
